import React, { useCallback, useEffect, useState } from 'react';
import { matchPath, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { setServiceNoticeHandler, setUnauthorizedHandler } from '../config/api';
import App from '../App';
import ServiceNotice from '../components/ServiceNotice';
import { Spinner } from '../ui';
import AuthScreens from './AuthScreens';
import { currentLanguage, followAccountLanguage, languageOnArrival, setAccountLanguageSaver, takeLoggedOutChoice } from '../i18n';
import { forgetSavedViews, setSavedViewsUser } from '../shell/savedViews';

// Link tokens are 64 hex characters. Anything else never reaches an API URL
// (a token like "..%2F..%2Finventories" would otherwise call another endpoint).
const TOKEN = /^[a-f0-9]{64}$/;
export const isToken = (token) => typeof token === 'string' && TOKEN.test(token);

// Old e-mail links (/?invite=T, /?set-password=T, /?reset=T) → their new paths;
// a malformed token → the login screen, which says the link is invalid.
export function legacyLinkTarget(search) {
  const params = new URLSearchParams(search);
  for (const [kind, path] of [['invite', '/invite/'], ['set-password', '/password/'], ['reset', '/password/']]) {
    const token = params.get(kind);
    if (token !== null) {
      return isToken(token) ? path + token : { pathname: '/login', state: { invalidLink: true } };
    }
  }
  return null;
}

const AUTH_PATHS = [
  ['/login', 'login'],
  ['/request-account', 'request'],
  ['/forgot', 'forgot'],
  ['/invite/:token', 'invite'],
  ['/password/:token', 'password'],
];

function authRoute(pathname) {
  for (const [pattern, mode] of AUTH_PATHS) {
    const match = matchPath(pattern, pathname);
    if (match) return { mode, token: match.params.token || null };
  }
  return null;
}

// Where to go after logging in: the page the user was trying to reach.
const returnPath = (state) => {
  const from = state && state.from;
  if (!from || !from.pathname || authRoute(from.pathname)) return '/';
  return from.pathname + (from.search || '') + (from.hash || '');
};

function AuthGate() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(undefined);
  const [notice, setNotice] = useState(null);

  // The account's language is applied before the user is set, so the first
  // logged-in render is already in it (no flash of the other language).
  const arrive = useCallback(async (u) => {
    if (!u) return u;
    const { show, save } = languageOnArrival(u.locale, { loggedOutChoice: takeLoggedOutChoice(), current: currentLanguage() });
    await followAccountLanguage(show);
    if (!save) return u;
    api.put('/auth/me/locale', { locale: show }).catch(() => {});
    return { ...u, locale: show };
  }, []);

  const refresh = useCallback(async () => {
    let me = null;
    try {
      me = (await api.get('/auth/me')).data.user;
    } catch (error) {
      me = null;
    }
    setUser(await arrive(me));
  }, [arrive]);

  useEffect(() => {
    // A session gone takes the saved views with it: whoever logs in next starts empty.
    setUnauthorizedHandler(() => { forgetSavedViews(); setUser(null); });
    setServiceNoticeHandler(setNotice);
    refresh();
  }, [refresh]);

  // The saved views held are this account's only.
  const userId = user ? user.id : null;
  useEffect(() => { setSavedViewsUser(userId); }, [userId]);

  // While logged in, the switcher also saves the choice on the account.
  const loggedIn = !!user;
  useEffect(() => {
    setAccountLanguageSaver(loggedIn
      ? (lng) => { api.put('/auth/me/locale', { locale: lng }).catch(() => {}); }
      : null);
  }, [loggedIn]);

  const onLoggedIn = useCallback(async (u) => {
    setUser(await arrive(u));
    navigate(returnPath(location.state), { replace: true });
  }, [arrive, navigate, location.state]);

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      forgetSavedViews();
      setUser(null);
      navigate('/login', { replace: true });
    }
  };

  // A release under way hides everything; an outdated bundle asks for a reload.
  if (notice === 'maintenance') {
    return <ServiceNotice kind="maintenance" />;
  }
  const outdated = notice === 'client_outdated' && <ServiceNotice kind="client_outdated" />;

  const legacy = legacyLinkTarget(location.search);
  if (legacy) {
    return typeof legacy === 'string'
      ? <Navigate to={legacy} replace />
      : <Navigate to={legacy.pathname} state={legacy.state} replace />;
  }
  if (user === undefined) {
    return <div className="app-loading"><Spinner size={24} label={t('common.loading')} /></div>;
  }

  const auth = authRoute(location.pathname);
  if (auth && auth.token !== null && !isToken(auth.token)) {
    return <Navigate to="/login" replace state={{ invalidLink: true }} />;
  }
  if (auth) {
    // Invitation and password links work whoever is logged in; the other
    // auth screens make no sense once logged in.
    if (user && (auth.mode === 'login' || auth.mode === 'request' || auth.mode === 'forgot')) {
      return <Navigate to={returnPath(location.state)} replace />;
    }
    return <>{outdated}<AuthScreens mode={auth.mode} token={auth.token} onLoggedIn={onLoggedIn} /></>;
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{outdated}<App user={user} onLogout={logout} /></>;
}

export default AuthGate;
